// src/components/Blocks/Listing/YearAccordionListingBlockTemplate.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { flattenToAppURL } from '@plone/volto/helpers';
import CommonItemRenderer from './CommonItemRenderer';
import './YearAccordionListingBlockTemplate.less';

/**
 * Year → Section accordions (EEA pattern)
 * - Years: plain headers
 * - Sections: EEA accordion markup/classes
 * - Untagged items: ABOVE the accordion
 * - If no tag mappings provided: list items grouped by year (no accordion)
 * - Toggle behaviour: DOM-based per item (like event block), no shared React state,
 *   to avoid cross-section interference when labels repeat across years.
 */

// --- helpers ---
const getYear = (item) => {
  const dateStr =
    item.start ||
    item.effective ||
    item.Date ||
    item.created ||
    item.modified;
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? 'Unknown' : String(d.getFullYear());
};

const getDateForSort = (item) => {
  const dateStr =
    item.start ||
    item.effective ||
    item.Date ||
    item.created ||
    item.modified;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseMappings = (mappingText = '') => {
  return mappingText
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const firstColon = line.indexOf(':');
      if (firstColon === -1) return null;
      const rawHeader = line.slice(0, firstColon).trim();
      let rawTags = line.slice(firstColon + 1).trim();
      if (!rawHeader || !rawTags) return null;

      // Allow both "Header: |tag-a, tag-b" and "Header: tag-a, tag-b"
      rawTags = rawTags.replace(/^\|/, '');
      const tags = rawTags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      return { header: rawHeader, tags };
    })
    .filter(Boolean);
};

const extractTags = (item) => {
  const raw =
    item.Subject ||
    item.subjects ||
    item.subject ||
    item.tags ||
    item['@components']?.subjects ||
    item['@components']?.subjects?.items ||
    [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr
    .filter(Boolean)
    .map((t) => {
      if (typeof t === 'string') return t;
      if (t && typeof t === 'object') {
        return t.title || t.token || t.id || String(t);
      }
      return String(t);
    })
    .filter(Boolean);
};

const slugify = (s) =>
  String(s)
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[\\u0300-\\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const hasAnyMappingTag = (item, tags) => {
  const itemTags = extractTags(item);
  if (!itemTags.length) return false;
  const lc = new Set(itemTags.map((t) => String(t).toLowerCase()));
  const sl = new Set(itemTags.map(slugify));
  return tags.some((t) => {
    const tl = String(t).toLowerCase();
    const ts = slugify(t);
    return lc.has(tl) || sl.has(ts);
  });
};

const groupByYear = (items) => {
  const byYear = {};
  items.forEach((it) => {
    const y = getYear(it);
    byYear[y] = byYear[y] || [];
    byYear[y].push(it);
  });
  
  // Sort items within each year by date (desc), then by original folder position
  Object.keys(byYear).forEach((year) => {
    byYear[year].sort((a, b) => {
      const dateA = getDateForSort(a);
      const dateB = getDateForSort(b);
      
      // Items with dates come before items without dates
      if (dateA && !dateB) return -1;
      if (!dateA && dateB) return 1;
      
      // If both have dates, sort by date (newest first)
      if (dateA && dateB) {
        const dateDiff = dateB.getTime() - dateA.getTime();
        if (dateDiff !== 0) return dateDiff;
      }
      
      // If dates are the same (or both null), sort by original folder order
      return (a._originalIndex || 0) - (b._originalIndex || 0);
    });
  });
  
  const years = Object.keys(byYear)
    .filter((y) => y !== 'Unknown')
    .map((y) => parseInt(y, 10))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => b - a)
    .map(String);
  if (byYear.Unknown) years.push('Unknown');
  return { years, byYear };
};

// Split items into mapped sections and unmatched list
const splitIntoSections = (items, mappings) => {
  const sections = {};
  const order = [];
  const unmatched = [];
  const ensure = (h) => {
    if (!sections[h]) {
      sections[h] = [];
      order.push(h);
    }
  };

  if (!mappings.length) {
    return { order: [], sections: {}, unmatched: items.slice() };
  }

  items.forEach((it) => {
    let placed = false;
    for (let i = 0; i < mappings.length; i++) {
      const { header, tags } = mappings[i];
      if (hasAnyMappingTag(it, tags)) {
        ensure(header);
        sections[header].push(it);
        placed = true;
        break;
      }
    }
    if (!placed) unmatched.push(it);
  });

  // Sort each section by date (desc), then by original folder order
  Object.keys(sections).forEach((header) => {
    sections[header].sort((a, b) => {
      const dateA = getDateForSort(a);
      const dateB = getDateForSort(b);
      
      if (dateA && !dateB) return -1;
      if (!dateA && dateB) return 1;
      
      if (dateA && dateB) {
        const dateDiff = dateB.getTime() - dateA.getTime();
        if (dateDiff !== 0) return dateDiff;
      }
      
      return (a._originalIndex || 0) - (b._originalIndex || 0);
    });
  });
  
  // Also sort unmatched items
  unmatched.sort((a, b) => {
    const dateA = getDateForSort(a);
    const dateB = getDateForSort(b);
    
    if (dateA && !dateB) return -1;
    if (!dateA && dateB) return 1;
    
    if (dateA && dateB) {
      const dateDiff = dateB.getTime() - dateA.getTime();
      if (dateDiff !== 0) return dateDiff;
    }
    
    return (a._originalIndex || 0) - (b._originalIndex || 0);
  });

  return { order, sections, unmatched };
};

export default function YearAccordionListingBlockTemplate(props) {
  const {
    items = [],

    // Extra fields from schema
    accordionTagGroups,
    accordionDefaultOpen = true, // open only the first section of the first year on initial render
    groupUnmatchedUnder = '', // optional heading above unmatched list

    // Display controls passed to CommonItemRenderer
    imageSide,
    imageWidth,
    howManyColumns,
    effectiveDate,
    expirationDate,
    titleTag,
    showDescription,
    eventDate,
    eventLocation,
    eventTime,
    showTitle,
    eventCard,
    quote,
    showRecurrence,
    fetchPriority,

    // Block identity (for unique ids)
    block,
    id,
  } = props;

  const instance = slugify(block || id || 'listing');

  const normalized = useMemo(
    () =>
      (items || []).map((it, idx) => {
        // Use Plone's folder position if available, otherwise use array index
        const folderPosition = typeof it.getObjPositionInParent === 'number' 
          ? it.getObjPositionInParent 
          : idx;
        
        return {
          ...it,
          url: flattenToAppURL(it['@id'] || it.url || ''),
          _originalIndex: folderPosition,
        };
      }),
    [items],
  );

  const { years, byYear } = useMemo(() => groupByYear(normalized), [normalized]);
  const mappings = useMemo(() => parseMappings(accordionTagGroups), [accordionTagGroups]);

  return (
    <div className="year-accordion-listing" data-variant="year-accordion-eea">
      {years.map((year, yearIdx) => {
        const list = byYear[year] || [];
        const { order, sections, unmatched } = splitIntoSections(list, mappings);

        // If no mappings: render flat list per year
        if (!mappings.length) {
          return (
            <section className="year-group" key={year}>
              <h3 className="year-heading">
                {year}{' '}
                <span className="eea-accordion__count" aria-hidden="true">
                  ({list.length})
                </span>
              </h3>
              <CommonItemRenderer
                items={list}
                showRecurrence={showRecurrence}
                quote={quote}
                showTitle={showTitle}
                eventCard={eventCard}
                titleTag={titleTag}
                eventDate={eventDate}
                eventTime={eventTime}
                eventLocation={eventLocation}
                showDescription={showDescription}
                effectiveDate={effectiveDate}
                expirationDate={expirationDate}
                imageSide={imageSide}
                imageWidth={imageWidth}
                howManyColumns={howManyColumns}
                fetchPriority={fetchPriority}
              />
            </section>
          );
        }

        const firstSlug = order[0] ? slugify(order[0]) : null;
        const yid = slugify(year);

        return (
          <section className="year-group" key={year}>
            <h3 className="year-heading">
              {year}{' '}
              <span className="eea-accordion__count" aria-hidden="true">
                ({list.length})
              </span>
            </h3>

            {/* Untagged above accordion */}
            {unmatched.length > 0 && (
              <div className="year-untagged">
                {groupUnmatchedUnder ? (
                  <h4 className="year-untagged-heading">{groupUnmatchedUnder}</h4>
                ) : null}
                <CommonItemRenderer
                  items={unmatched}
                  showRecurrence={showRecurrence}
                  quote={quote}
                  showTitle={showTitle}
                  eventCard={eventCard}
                  titleTag={titleTag}
                  eventDate={eventDate}
                  eventTime={eventTime}
                  eventLocation={eventLocation}
                  showDescription={showDescription}
                  effectiveDate={effectiveDate}
                  expirationDate={expirationDate}
                  imageSide={imageSide}
                  imageWidth={imageWidth}
                  howManyColumns={howManyColumns}
                  fetchPriority={fetchPriority}
                />
              </div>
            )}

            {/* Tagged sections as EEA accordion */}
            {order.length === 0 ? null : (
              <div className="eea-accordion" data-accordion-group={`year-${yid}-${instance}`}>
                {order.map((header, idx) => {
                  const slug = slugify(header);
                  const key = `${year}::${slug}`;
                  const openInit = accordionDefaultOpen && yearIdx === 0 && idx === 0;

                  const triggerId = `acc-trigger-${instance}-${yid}-${slug}`;
                  const panelId = `acc-panel-${instance}-${yid}-${slug}`;

                  return (
                    <section
                      key={key}
                      className={`eea-accordion__item ${openInit ? 'is-open' : ''}`}
                    >
                      <h4 className="eea-accordion__header">
                        <button
                          id={triggerId}
                          className="eea-accordion__trigger"
                          aria-expanded={openInit}
                          aria-controls={panelId}
                          onClick={(e) => {
                          const item = e.currentTarget.closest('.eea-accordion__item');
                          const panel = item ? item.querySelector('.eea-accordion__panel') : null;
                          const expanded =
                            e.currentTarget.getAttribute('aria-expanded') === 'true';
                          e.currentTarget.setAttribute('aria-expanded', String(!expanded));
                          if (panel) panel.hidden = expanded;
                          if (item) item.classList.toggle('is-open');
                        }}
                          type="button"
                        >
                          <span className="eea-accordion__title">
                            {header}{' '}
                            <span className="eea-accordion__count" aria-hidden="true">
                              ({(sections[header] || []).length})
                            </span>
                          </span>
                          <span className="eea-accordion__chevron" aria-hidden="true" />
                        </button>
                      </h4>

                      <div
                        id={panelId}
                        className="eea-accordion__panel"
                        role="region"
                        aria-labelledby={triggerId}
                        hidden={!openInit}
                      >
                        <CommonItemRenderer
                          items={sections[header] || []}
                          showRecurrence={showRecurrence}
                          quote={quote}
                          showTitle={showTitle}
                          eventCard={eventCard}
                          titleTag={titleTag}
                          eventDate={eventDate}
                          eventTime={eventTime}
                          eventLocation={eventLocation}
                          showDescription={showDescription}
                          effectiveDate={effectiveDate}
                          expirationDate={expirationDate}
                          imageSide={imageSide}
                          imageWidth={imageWidth}
                          howManyColumns={howManyColumns}
                          fetchPriority={fetchPriority}
                        />
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

YearAccordionListingBlockTemplate.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  accordionTagGroups: PropTypes.string,
  accordionDefaultOpen: PropTypes.bool,
  groupUnmatchedUnder: PropTypes.string,
  imageSide: PropTypes.string,
  imageWidth: PropTypes.number,
  howManyColumns: PropTypes.number,
  effectiveDate: PropTypes.bool,
  expirationDate: PropTypes.bool,
  titleTag: PropTypes.string,
  showDescription: PropTypes.bool,
  eventDate: PropTypes.bool,
  eventTime: PropTypes.bool,
  eventLocation: PropTypes.bool,
  showTitle: PropTypes.bool,
  eventCard: PropTypes.bool,
  quote: PropTypes.bool,
  showRecurrence: PropTypes.bool,
  fetchPriority: PropTypes.string,
  block: PropTypes.string,
  id: PropTypes.string,
};